import { defineComponent, ref, computed, watch, onBeforeUnmount, Transition } from 'vue';
import CloseOutlined from '@ant-design/icons-vue/CloseOutlined';
import FileTextOutlined from '@ant-design/icons-vue/FileTextOutlined';
import classNames from '../_util/classNames';
import { getTransitionProps } from '../_util/transition';
import FloatButton, { floatButtonPrefixCls } from './FloatButton';
import useConfigInject from '../config-provider/hooks/useConfigInject';
import { useProvideFloatButtonGroupContext } from './context';
import { findDOMNode, initDefaultProps } from '../_util/props-util';
import { floatButtonGroupProps } from './interface';
import canUseDom from '../_util/canUseDom';
// CSSINJS
import useStyle from './style';
import useMergedState from '../_util/hooks/useMergedState';
const FloatButtonGroup = defineComponent({
    compatConfig: { MODE: 3 },
    name: 'AFloatButtonGroup',
    inheritAttrs: false,
    props: initDefaultProps(floatButtonGroupProps(), {
        type: 'default',
        shape: 'circle',
        placement: 'top',
    }),
    setup(props, { attrs, slots, emit }) {
        const { prefixCls, direction } = useConfigInject(floatButtonPrefixCls, props);
        // style
        const [wrapSSR, hashId] = useStyle(prefixCls);
        const [open, setOpen] = useMergedState(false, { value: computed(() => props.open) });
        const floatButtonGroupRef = ref(null);
        const floatButtonRef = ref(null);
        useProvideFloatButtonGroupContext({
            shape: computed(() => props.shape),
        });
        const hoverTypeAction = {
            onMouseenter() {
                var _a;
                setOpen(true);
                emit('update:open', true);
                (_a = props.onOpenChange) === null || _a === void 0 ? void 0 : _a.call(props, true);
            },
            onMouseleave() {
                var _a;
                setOpen(false);
                emit('update:open', false);
                (_a = props.onOpenChange) === null || _a === void 0 ? void 0 : _a.call(props, false);
            },
        };
        const hoverAction = computed(() => {
            return props.trigger === 'hover' ? hoverTypeAction : {};
        });
        const handleOpenChange = () => {
            var _a;
            const nextOpen = !open.value;
            emit('update:open', nextOpen);
            (_a = props.onOpenChange) === null || _a === void 0 ? void 0 : _a.call(props, nextOpen);
            setOpen(nextOpen);
        };
        const onClick = (e) => {
            var _a, _b, _c;
            if ((_a = floatButtonGroupRef.value) === null || _a === void 0 ? void 0 : _a.contains(e.target)) {
                if ((_b = findDOMNode(floatButtonRef.value)) === null || _b === void 0 ? void 0 : _b.contains(e.target)) {
                    handleOpenChange();
                }
                return;
            }
            setOpen(false);
            emit('update:open', false);
            (_c = props.onOpenChange) === null || _c === void 0 ? void 0 : _c.call(props, false);
        };
        watch(computed(() => props.trigger), value => {
            if (!canUseDom()) {
                return;
            }
            document.removeEventListener('click', onClick);
            if (value === 'click') {
                document.addEventListener('click', onClick);
            }
        }, { immediate: true });
        onBeforeUnmount(() => {
            document.removeEventListener('click', onClick);
        });
        return () => {
            var _a, _b, _c;
            const { shape = 'circle', type = 'default', tooltip, description, trigger, placement = 'top', closeIcon, htmlType, } = props;
            const groupPrefixCls = `${prefixCls.value}-group`;
            const isMenuMode = !!(trigger && ['click', 'hover'].includes(trigger));
            const isValidPlacement = !!placement && ['top', 'left', 'right', 'bottom'].includes(placement);
            const groupCls = classNames(groupPrefixCls, hashId.value, attrs.class, {
                [`${groupPrefixCls}-rtl`]: direction.value === 'rtl',
                [`${groupPrefixCls}-${shape}`]: shape,
                [`${groupPrefixCls}-${shape}-shadow`]: !isMenuMode,
                [`${groupPrefixCls}-${placement}`]: isMenuMode && isValidPlacement,
            });
            const wrapperCls = classNames(hashId.value, `${groupPrefixCls}-wrap`);
            const transitionProps = getTransitionProps(`${groupPrefixCls}-wrap`);
            const mergedCloseIcon = (_b = closeIcon !== null && closeIcon !== void 0 ? closeIcon : (_a = slots.closeIcon) === null || _a === void 0 ? void 0 : _a.call(slots)) !== null && _b !== void 0 ? _b : <CloseOutlined />;
            return wrapSSR(<div ref={floatButtonGroupRef} {...attrs} class={groupCls} {...hoverAction.value}>
          {isMenuMode ? (<>
              <Transition {...transitionProps}>
                <div v-show={open.value} class={wrapperCls}>
                  {slots.default && slots.default()}
                </div>
              </Transition>
              <FloatButton ref={floatButtonRef} type={type} shape={shape} tooltip={tooltip} description={description} htmlType={htmlType} v-slots={{
                        icon: () => { var _a; return open.value ? mergedCloseIcon : ((_a = slots.icon) === null || _a === void 0 ? void 0 : _a.call(slots)) || <FileTextOutlined />; },
                        tooltip: slots.tooltip,
                        description: slots.description,
                    }}></FloatButton>
            </>) : ((_c = slots.default) === null || _c === void 0 ? void 0 : _c.call(slots))}
        </div>);
        };
    },
});
export default FloatButtonGroup;
